import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { BaseChartComponent } from "./base-chart.component";
import { ChartConfiguration, Plugin } from "chart.js/auto";
import { PRIMARY_LINE_COLOR } from "src/app/constants/index";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-humidity-chart",
  templateUrl: "./chart.component.html",
  styleUrls: ["./chart.component.scss"],
})
export class HumidityChartComponent extends BaseChartComponent implements OnChanges {
  private calculatedYMin: number = 0;
  private calculatedYMax: number = 100;

  constructor(translate: TranslateService, cdr: ChangeDetectorRef) {
    super(translate, cdr);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Update the data property for app-empty-data component
    if (this.data?.length) {
      // Force chart re-rendering when data changes
      if (changes.data && this.chartContainer) {
        setTimeout(() => {
          this.renderChart();
          this.cdr.detectChanges();
        }, 0);
        return;
      }
    }

    // Call parent's ngOnChanges to ensure base component logic runs
    super.ngOnChanges(changes);
  }

  /**
   * Calculate Y-axis limits with a margin of 5 units above and below the min and max humidity values.
   */
  private calculateYAxisLimits(minHum: number, maxHum: number): { min: number; max: number } {
    let ySuggestedMin = Math.floor(minHum / 5) * 5;
    let ySuggestedMax = Math.ceil(maxHum / 5) * 5;

    // Ensure there's a visible range if all data points are the same multiple of 5
    ySuggestedMin -= 5;
    ySuggestedMax += 5;

    return {
      min: ySuggestedMin < 0 ? 0 : ySuggestedMin,
      max: ySuggestedMax,
    };
  }

  protected getChartConfig(): ChartConfiguration {
    const transformedData = this.data.map(point => {
      const timestamp = new Date(point.timestamp).getTime();
      if (!(point.value || point.value === 0)) {
        return { x: timestamp, y: null };
      }
      return { x: timestamp, y: point.value * 100 };
    });

    const xSuggestedMax = !this.currentTime ? new Date().getTime() : null;

    // Calculate dynamic y-axis limits based on transformed data
    const validYValues = transformedData.map(point => point.y).filter(value => value !== null);

    let ySuggestedMin: number;
    let ySuggestedMax: number;

    if (validYValues.length > 0) {
      const minHum = Math.min(...validYValues);
      const maxHum = Math.max(...validYValues);

      // Apply the new margin rules
      const limits = this.calculateYAxisLimits(minHum, maxHum);
      ySuggestedMin = limits.min;
      ySuggestedMax = limits.max;
    } else {
      // Default values when there's no data (already include the margin of 5)
      ySuggestedMin = 0;
      ySuggestedMax = 100;
    }

    this.calculatedYMin = ySuggestedMin;
    this.calculatedYMax = ySuggestedMax;

    // Get common options
    const commonOptions = this.getCommonChartOptions(xSuggestedMax, ySuggestedMax, "%", ySuggestedMin);

    return {
      type: "line",
      data: {
        datasets: [
          {
            data: transformedData,
            borderColor: PRIMARY_LINE_COLOR,
            backgroundColor: PRIMARY_LINE_COLOR,
            borderWidth: 2,
            pointRadius: 1,
            tension: 0.5,
            parsing: false,
          },
        ],
      },
      options: {
        ...commonOptions,
        scales: {
          ...commonOptions.scales,
          y: {
            type: "linear",
            min: ySuggestedMin,
            max: ySuggestedMax,
            grid: commonOptions.scales.y.grid,
            ticks: {
              ...commonOptions.scales.y.ticks,
              stepSize: 5,
            },
          },
        },
      },
    };
  }
}
